import React from "react";
import {
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  SelectChangeEvent,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { useTranslation } from "react-i18next";
import { Project, ProjectBoard, Account } from "../types";
import { getProjectStatusColor } from "../utils/statusColors";
import dayjs, { Dayjs } from "dayjs";

interface ProjectFormProps {
  form: Partial<Project>;
  isEditing: boolean;
  isNew?: boolean;
  boards?: ProjectBoard[];
  accounts?: Account[];
  projectStatuses?: string[];
  handleTextChange: (
    field: keyof Partial<Project>,
  ) => (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleSelectChange: (
    field: keyof Partial<Project>,
  ) => (e: SelectChangeEvent<string>) => void;
  handleDateChange: (
    field: keyof Partial<Project>,
  ) => (newValue: Dayjs | null) => void;
  handleNumberChange?: (
    field: keyof Partial<Project>,
  ) => (e: React.ChangeEvent<HTMLInputElement>) => void;
  progressError?: string | null;
}

const ProjectForm: React.FC<ProjectFormProps> = ({
  form,
  isEditing,
  isNew = false,
  boards = [],
  accounts = [],
  projectStatuses = [],
  handleTextChange,
  handleSelectChange,
  handleDateChange,
  handleNumberChange,
  progressError,
}) => {
  const { t } = useTranslation();

  const invalidStart = !!form.dateStart && !dayjs(form.dateStart).isValid();
  const invalidEnd = !!form.dateEnd && !dayjs(form.dateEnd).isValid();

  const startDate = form.dateStart ? dayjs(form.dateStart) : null;
  const endDate = form.dateEnd ? dayjs(form.dateEnd) : null;

  return (
    <Box component="form" noValidate sx={{ width: "100%" }}>
      <Box display="flex" flexDirection="column" gap={0}>
        <TextField
          label={t("projects.detail.nameLabel") || "Название"}
          fullWidth
          margin="normal"
          value={form.name || ""}
          onChange={handleTextChange("name")}
          disabled={!isEditing}
          size="small"
          required
        />

        {isNew && (
          <FormControl fullWidth margin="normal" size="small" required>
            <InputLabel>
              {t("projects.detail.boardLabel") || "Доска"}
            </InputLabel>
            <Select
              label={t("projects.detail.boardLabel") || "Доска"}
              value={form.boardId || ""}
              onChange={handleSelectChange("boardId")}
              disabled={!isEditing}
            >
              {boards.map((board) => (
                <MenuItem key={board.id} value={board.id}>
                  {board.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {!isNew && form.boardName && (
          <TextField
            label={t("projects.detail.boardLabel") || "Доска"}
            fullWidth
            margin="normal"
            value={form.boardName}
            disabled
            size="small"
          />
        )}

        <FormControl fullWidth margin="normal" size="small">
          <InputLabel>
            {t("projects.detail.accountLabel") || "Контрагент"}
          </InputLabel>
          <Select
            label={t("projects.detail.accountLabel") || "Контрагент"}
            value={form.accountId || ""}
            onChange={handleSelectChange("accountId")}
            disabled={!isEditing}
          >
            <MenuItem value="">
              <em>{t("typeOptions.all") || "Не выбран"}</em>
            </MenuItem>
            {accounts.map((account) => (
              <MenuItem key={account.id} value={account.id}>
                {account.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            gap: 0,
          }}
        >
          <FormControl fullWidth margin="normal" size="small">
            <InputLabel
              sx={{
                color: form.status
                  ? getProjectStatusColor(form.status)
                  : "inherit",
                "&.Mui-focused": {
                  color: form.status
                    ? getProjectStatusColor(form.status)
                    : "inherit",
                },
              }}
            >
              {t("projects.detail.statusLabel") || "Статус"}
            </InputLabel>
            <Select
              label={t("projects.detail.statusLabel") || "Статус"}
              value={form.status || projectStatuses[0] || ""}
              onChange={handleSelectChange("status")}
              disabled={!isEditing}
              sx={{
                color: form.status
                  ? getProjectStatusColor(form.status)
                  : "inherit",
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: form.status
                    ? getProjectStatusColor(form.status)
                    : "inherit",
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: form.status
                    ? getProjectStatusColor(form.status)
                    : "inherit",
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: form.status
                    ? getProjectStatusColor(form.status)
                    : "inherit",
                },
              }}
            >
              {projectStatuses.length > 0 ? (
                projectStatuses.map((status) => {
                  const normalizedStatus = status
                    .toLowerCase()
                    .replace(/\s/g, "_");
                  const translationKey = `statuses.project.${normalizedStatus}`;
                  const displayText = t(translationKey, status);
                  return (
                    <MenuItem
                      key={status}
                      value={status}
                      sx={{ color: getProjectStatusColor(status) }}
                    >
                      {displayText}
                    </MenuItem>
                  );
                })
              ) : (
                <MenuItem disabled>
                  {t("projects.loading") || "Загрузка статусов..."}
                </MenuItem>
              )}
            </Select>
          </FormControl>
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            gap: { xs: 0, sm: 2 },
          }}
        >
          <DatePicker
            label={t("projects.detail.startDateLabel") || "Дата начала"}
            value={startDate}
            onChange={handleDateChange("dateStart")}
            disabled={!isEditing}
            reduceAnimations={true}
            slotProps={{
              textField: {
                margin: "normal",
                size: "small",
                fullWidth: true,
                error: invalidStart,
                helperText: invalidStart
                  ? t("projects.detail.invalidStartDate") || "Некорректная дата"
                  : "",
              },
              actionBar: {
                actions: ["today", "cancel", "accept"],
                sx: {
                  "& .MuiButton-root": {
                    fontWeight: "bold",
                    textTransform: "uppercase",
                    color: "#fff",
                    minWidth: 80,
                  },
                  "& .MuiButton-root:nth-of-type(1)": {
                    // Сегодня
                    backgroundColor: "#4467A5",
                    "&:hover": {
                      backgroundColor: "#385894",
                    },
                  },
                  "& .MuiButton-root:nth-of-type(2)": {
                    // Отмена
                    backgroundColor: "#b71c1c",
                    "&:hover": {
                      backgroundColor: "#9a0007",
                    },
                  },
                  "& .MuiButton-root:nth-of-type(3)": {
                    // Сохранить
                    backgroundColor: "#2e7d32",
                    "&:hover": {
                      backgroundColor: "#1b5e20",
                    },
                  },
                },
              },
            }}
          />
          <DatePicker
            label={t("projects.detail.endDateLabel") || "Дата окончания"}
            value={endDate}
            onChange={handleDateChange("dateEnd")}
            disabled={!isEditing}
            reduceAnimations={true}
            slotProps={{
              textField: {
                margin: "normal",
                size: "small",
                fullWidth: true,
                error: invalidEnd,
                helperText: invalidEnd
                  ? t("projects.detail.invalidEndDate") || "Некорректная дата"
                  : "",
              },
              actionBar: {
                actions: ["today", "cancel", "accept"],
                sx: {
                  "& .MuiButton-root": {
                    fontWeight: "bold",
                    textTransform: "uppercase",
                    color: "#fff",
                    minWidth: 80,
                  },
                  "& .MuiButton-root:nth-of-type(1)": {
                    // Сегодня
                    backgroundColor: "#4467A5",
                    "&:hover": {
                      backgroundColor: "#385894",
                    },
                  },
                  "& .MuiButton-root:nth-of-type(2)": {
                    // Отмена
                    backgroundColor: "#b71c1c",
                    "&:hover": {
                      backgroundColor: "#9a0007",
                    },
                  },
                  "& .MuiButton-root:nth-of-type(3)": {
                    // Сохранить
                    backgroundColor: "#2e7d32",
                    "&:hover": {
                      backgroundColor: "#1b5e20",
                    },
                  },
                },
              },
            }}
          />
        </Box>

        <TextField
          label={t("projects.detail.progressLabel") || "Прогресс (%)"}
          fullWidth
          margin="normal"
          type="number"
          value={form.cProgres ?? ""}
          onChange={handleNumberChange?.("cProgres") || (() => {})}
          disabled={!isEditing}
          size="small"
          error={!!progressError}
          helperText={
            progressError ||
            t("projects.detail.progressPlaceholder") ||
            "От 0 до 100"
          }
          inputProps={{ min: 0, max: 100 }}
        />

        <TextField
          label={t("projects.detail.descriptionLabel") || "Описание"}
          fullWidth
          margin="normal"
          value={form.description || ""}
          onChange={handleTextChange("description")}
          disabled={!isEditing}
          size="small"
          multiline
          rows={4}
        />
      </Box>
    </Box>
  );
};

export default ProjectForm;
